import { Box } from "@mui/material"
import { styled } from "@mui/material/styles"
import { Editor } from "@tinymce/tinymce-react"
import React, { useEffect, useState, useRef } from "react"
import { IApiFunc, IApiResult } from "auth/interface/api.interface"
import { useApi } from "auth/useApi"
import { apiEndpoints } from "generated/apiEndpoints"
import { addSasKeyToImageSrc } from "pages/documents/helpers/imageSrcSasKey"
import { tinyMceApiKey } from "./TinyMCE"

interface TinyMCEViewerProps {
    initVal: string
    editorRef: React.MutableRefObject<any | null>
}

const StyledBox = styled(Box)({
    "& .tox-tinymce": {
        border: "none"
    }
})

export const TinyMCEViewer = ({ initVal, editorRef }: TinyMCEViewerProps) => {
    const [sasKeyResult, sasKeyApi] = useApi() as [IApiResult, IApiFunc]
    const [initialValue, setInitialValue] = useState<string>()
    const containerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        sasKeyApi.get(apiEndpoints.documents.getsaskey)
    }, [])

    useEffect(() => {
        if (sasKeyResult.status !== 2) return
        if (initVal === null) {
            setInitialValue("")
            return
        }
        setInitialValue(addSasKeyToImageSrc(initVal, sasKeyResult.data))
    }, [sasKeyResult, initVal])

    useEffect(() => {
        if (editorRef.current) {
            const editor = editorRef.current
            const body = editor.getBody()
            if (body) {
                body.setAttribute("inputmode", "none")
            }

            const iframe = editor?.iframeElement as HTMLIFrameElement | null
            if (iframe && iframe.contentDocument) {
                const iframeDoc = iframe.contentDocument
                const links = iframeDoc.querySelectorAll('a')
                const style = iframeDoc.createElement('style')
                style.textContent = `
            a {
              cursor: pointer !important;
              color: #1976d2;
              text-decoration: underline;
            }
            a:hover {
              color: #1565c0 !important;
            }
          `
                iframeDoc.head.appendChild(style)

                links.forEach((link: HTMLElement) => {
                    const href = link.getAttribute('href')
                    if (href) {
                        const isExternal = !href.startsWith('/') && !href.startsWith(window.location.origin)
                        const needsProtocol = isExternal && !href.startsWith('http')

                        if (needsProtocol) {
                            link.setAttribute('href', `https://${href}`)
                        }

                        if (isExternal) {
                            link.setAttribute('target', '_blank')
                            link.setAttribute('rel', 'noopener noreferrer')
                        }

                        link.addEventListener('click', (e: Event) => {
                            e.preventDefault()
                            const finalUrl = link.getAttribute('href')
                            if (finalUrl) {
                                window.open(finalUrl, '_blank', 'noopener,noreferrer')
                            }
                        })
                    }
                })
            }
        }
    }, [initialValue, editorRef, editorRef?.current?.iframeElement])

    return (
        <StyledBox ref={containerRef}>
            <Editor
                apiKey={tinyMceApiKey}
                onInit={(_evt, editor) => editorRef.current = editor}
                initialValue={initialValue}
                disabled={true}
                init={{
                    height: 500,
                    menubar: false,
                    toolbar: false,
                    branding: false,
                    highlight_on_focus: false,
                    statusbar: false,
                    disabled: true,
                    content_style: `
                        body {
                            cursor: default;
                            caret-color: transparent;
                        }
                        a, a * { cursor: pointer !important; }
                    `
                }}
            />
        </StyledBox>
    )
}
